import { memo } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { 
  FileText, 
  CheckCircle, 
  Clock, 
  Edit,
  Trash2,
  UserX,
  UserCheck,
  MapPin,
  Plus
} from "lucide-react";
import { useTranslation } from 'react-i18next';

// Map designation to readable label
const getDesignationLabel = (designation) => {
  const labels = {
    tahsildar: "Tahsildar",
    deputy_tahsildar: "Deputy Tahsildar",
    shirestedar: "Shirestedar",
    revenue_inspector: "Revenue Inspector",
    village_accountant: "Village Accountant",
    first_division_clerk: "First Division Clerk",
    second_division_clerk: "Second Division Clerk",
    ddlr: "DDLR",
    adlr: "ADLR",
    surveyor: "Surveyor",
  };
  return labels[designation] || designation;
};

// Format circle name for display
const formatCircleName = (circle) => {
  if (!circle) return null;
  return circle.split('_').map(word => 
    word.charAt(0).toUpperCase() + word.slice(1)
  ).join(' ');
};

// Get circle assignments in normalized format (handles both old and new data format)
const getCircleAssignments = (official) => {
  // New format: circle_assignments array
  if (official.circle_assignments && official.circle_assignments.length > 0) {
    return official.circle_assignments;
  }
  // Old format: single assigned_circle field
  if (official.assigned_circle) {
    return [{
      hobli: official.assigned_hobli,
      circle: official.assigned_circle
    }];
  }
  return [];
};

const OfficialCard = memo(function OfficialCard({ 
  official, 
  onEdit, 
  onToggleStatus, 
  onDelete,
  onAssignCircle,
  onRemoveCircle,
  userDesignation
}) {
  const { t } = useTranslation();
  const isDisabled = official.is_disabled;
  const isVA = official.designation === "village_accountant";
  const isTahsildar = userDesignation === "tahsildar";
  
  // Get all circle assignments
  const circleAssignments = getCircleAssignments(official);
  const hasAssignments = circleAssignments.length > 0;
  
  return (
    <Card 
      className={`p-4 border transition-all ${isDisabled ? 'bg-stone-100 border-stone-300 opacity-75' : 'bg-white border-stone-200 hover:shadow-md'}`}
      data-testid={`official-card-${official.id}`}
    >
      <div className="flex items-start justify-between mb-3">
        <div className="flex-1">
          <div className="flex items-center gap-2">
            <h4 className="font-semibold text-slate-900">{official.name}</h4>
            {isDisabled && (
              <Badge variant="outline" className="bg-red-50 text-red-700 border-red-200 text-xs">
                {t('officialsManagement.disabled')}
              </Badge>
            )}
          </div>
          <p className="text-sm text-slate-500">{official.email}</p>
        </div>
        <Badge 
          className={`text-xs ${
            official.department === "revenue" 
              ? "bg-emerald-100 text-emerald-800 border-emerald-200" 
              : "bg-blue-100 text-blue-800 border-blue-200"
          }`}
        >
          {getDesignationLabel(official.designation)}
        </Badge>
      </div>
      
      <div className="text-xs text-slate-500 mb-3">
        {t('officialsManagement.phone')}: {official.phone || "-"}
      </div>
      
      {/* Hobli Assignment for Revenue Inspectors */}
      {official.designation === "revenue_inspector" && official.assigned_hobli && (
        <div className="mb-3 p-2 bg-purple-50 border border-purple-200 rounded-lg">
          <div className="flex items-center gap-2">
            <MapPin className="w-4 h-4 text-purple-600" />
            <span className="text-xs font-medium text-purple-800">
              {t('officialsManagement.hobliLabel') || 'Hobli'}:
            </span>
            <span className="text-sm font-medium text-purple-900">
              {official.assigned_hobli}
            </span>
          </div>
        </div>
      )}
      
      {/* Circle Assignments for Village Accountants */}
      {isVA && (
        <div className="mb-3 p-2 bg-amber-50 border border-amber-200 rounded-lg">
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-2">
              <MapPin className="w-4 h-4 text-amber-600" />
              <span className="text-xs font-medium text-amber-800">
                {t('officialsManagement.assignedCircles') || 'Assigned Circles'}
                {hasAssignments && <span className="ml-1">({circleAssignments.length})</span>}
              </span>
            </div>
            {isTahsildar && onAssignCircle && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => onAssignCircle(official)}
                className="text-amber-700 hover:text-amber-800 hover:bg-amber-100 border-amber-300 h-7 px-2"
                data-testid={`assign-circle-${official.id}`}
              >
                <Plus className="w-3 h-3 mr-1" />
                {t('officialsManagement.addCircle') || 'Add'}
              </Button>
            )}
          </div>
          
          {hasAssignments ? (
            <div className="space-y-1">
              {circleAssignments.map((assignment, index) => {
                const circleValue = typeof assignment === 'string' ? assignment : (assignment?.circle || '');
                const hobliValue = typeof assignment === 'string' ? '' : (assignment?.hobli || '');
                return (
                  <div 
                    key={`${hobliValue}-${circleValue}-${index}`}
                    className="flex items-center justify-between bg-white rounded px-2 py-1 border border-amber-100"
                  >
                    <div className="text-sm">
                      <span className="font-medium text-amber-900">
                        {formatCircleName(circleValue)}
                      </span>
                      {hobliValue && (
                        <span className="text-xs text-amber-600 ml-1">
                          ({hobliValue})
                        </span>
                      )}
                    </div>
                    {isTahsildar && onRemoveCircle && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => onRemoveCircle(official, assignment)}
                        className="text-red-500 hover:text-red-700 hover:bg-red-50 h-6 w-6 p-0"
                        data-testid={`remove-circle-${official.id}-${index}`}
                      >
                        <Trash2 className="w-3 h-3" />
                      </Button>
                    )}
                  </div>
                );
              })}
            </div>
          ) : (
            <span className="text-sm text-amber-600 italic">
              {t('officialsManagement.noCircleAssigned') || 'Not assigned'}
            </span>
          )}
        </div>
      )}
      
      {/* Workload Stats */}
      <div className="grid grid-cols-3 gap-2 mb-4">
        <div className="text-center p-2 bg-stone-50 rounded-lg">
          <div className="flex items-center justify-center gap-1 text-slate-600">
            <FileText className="w-3 h-3" />
            <span className="font-semibold">{official.workload?.total_assigned || 0}</span>
          </div>
          <p className="text-xs text-slate-500">{t('officialsManagement.totalAssigned')}</p>
        </div>
        <div className="text-center p-2 bg-yellow-50 rounded-lg">
          <div className="flex items-center justify-center gap-1 text-yellow-600">
            <Clock className="w-3 h-3" />
            <span className="font-semibold">{official.workload?.pending || 0}</span>
          </div>
          <p className="text-xs text-slate-500">{t('officialsManagement.pending')}</p>
        </div>
        <div className="text-center p-2 bg-emerald-50 rounded-lg">
          <div className="flex items-center justify-center gap-1 text-emerald-600">
            <CheckCircle className="w-3 h-3" />
            <span className="font-semibold">{official.workload?.completed || 0}</span>
          </div>
          <p className="text-xs text-slate-500">{t('officialsManagement.completed')}</p>
        </div>
      </div>
      
      {/* Action Buttons */}
      <div className="flex items-center gap-2 pt-3 border-t border-stone-200">
        <Button
          variant="outline"
          size="sm"
          onClick={() => onEdit(official)}
          className="flex-1 text-blue-600 hover:text-blue-700 hover:bg-blue-50"
          data-testid={`edit-official-${official.id}`}
        >
          <Edit className="w-3 h-3 mr-1" />
          {t('officialsManagement.edit')}
        </Button>
        <Button
          variant="outline"
          size="sm"
          onClick={() => onToggleStatus(official)}
          className={`flex-1 ${isDisabled ? 'text-emerald-600 hover:text-emerald-700 hover:bg-emerald-50' : 'text-orange-600 hover:text-orange-700 hover:bg-orange-50'}`}
          data-testid={`toggle-official-${official.id}`}
        >
          {isDisabled ? (
            <>
              <UserCheck className="w-3 h-3 mr-1" />
              {t('officialsManagement.enable')}
            </>
          ) : (
            <>
              <UserX className="w-3 h-3 mr-1" />
              {t('officialsManagement.disable')}
            </>
          )}
        </Button>
        <Button
          variant="outline"
          size="sm"
          onClick={() => onDelete(official)}
          className="text-red-600 hover:text-red-700 hover:bg-red-50"
          data-testid={`delete-official-${official.id}`}
        >
          <Trash2 className="w-3 h-3" />
        </Button>
      </div>
    </Card>
  );
});

export default OfficialCard;
